import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { AddNewIssuedCheckContext, AddNewIssuedCheckContextProps, FormValues, ICheck } from './index.context'
import { useForm, useWatch } from 'react-hook-form'
import { defaultValues } from './index.constant'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { useTranslation } from 'react-i18next'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { addNewIssuedCheck } from 'src/app/shared/services/IssuedCheckService'
import { notification } from 'antd'
import { IAddIssuedCheck } from 'src/app/shared/models/issuedCheck.model'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import lodash from 'lodash'
import { useClient } from 'src/app/shared/hooks/useClient'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'
import dayjs from 'dayjs'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { defaultColumnMinWidth } from 'src/app/shared/components/common/new-table/table.constants'

const AddNewIssuedCheckProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const { role } = useSelector(getUserProfile)
  const { t } = useTranslation()
  const { accounts, clients, loadingClients, handleGetAccounts, handleGetClients, handleGetClientInfoByUser } =
    useClient()
  const [checkList, setCheckList] = useState<AddNewIssuedCheckContextProps['checkList']>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [api, contextHolder] = notification.useNotification()
  const form = useForm<FormValues>({
    defaultValues,
  })

  const clientId =
    useWatch({
      control: form.control,
      name: 'clientId',
    }) ?? ''

  const accountId =
    useWatch({
      control: form.control,
      name: 'accountId',
    }) ?? ''

  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`AddNewIssuedCheck:table.${field}`)
    },
    [t],
  )

  const columnDefs: TableColumnDefs[] = useMemo(
    (): TableColumnDefs[] => [
      {
        colId: 'no',
        field: 'no',
        headerName: getHeaderName('no'),
        hide: false,
        defaultSelect: true,
        resizable: false,
        flex: undefined,
        sortable: false,
        headerClass: 'custom-col-resize-indicator',
        width: 20,
      },
      {
        colId: 'accountName',
        field: 'accountName',
        headerName: getHeaderName('accountName'),
        hide: false,
        defaultSelect: true,
        dataType: 'text',
        resizable: false,
        flex: undefined,
        sortable: false,
        headerClass: 'custom-col-resize-indicator',
        minWidth: defaultColumnMinWidth,
      },
      {
        colId: 'checkNumber',
        field: 'checkNumber',
        headerName: getHeaderName('checkNumber'),
        hide: false,
        defaultSelect: true,
        resizable: false,
        flex: undefined,
        sortable: false,
        headerClass: 'custom-col-resize-indicator',
        minWidth: defaultColumnMinWidth,
      },
      {
        colId: 'amount',
        field: 'amount',
        headerName: getHeaderName('amount'),
        hide: false,
        defaultSelect: true,
        resizable: false,
        flex: undefined,
        sortable: false,
        headerClass: 'custom-col-resize-indicator',
        minWidth: defaultColumnMinWidth,
      },
      {
        colId: 'issuedDate',
        field: 'issuedDate',
        headerName: getHeaderName('issuedDate'),
        hide: false,
        defaultSelect: true,
        resizable: false,
        flex: undefined,
        sortable: false,
        headerClass: 'custom-col-resize-indicator',
        minWidth: defaultColumnMinWidth,
      },
      {
        colId: 'issuedPayee',
        field: 'issuedPayee',
        headerName: getHeaderName('issuedPayee'),
        hide: false,
        defaultSelect: true,
        resizable: false,
        flex: undefined,
        sortable: false,
        headerClass: 'custom-col-resize-indicator',
        minWidth: defaultColumnMinWidth,
      },
    ],
    [getHeaderName],
  )

  const handleAutoIncrement = useCallback(
    (autoIncrement: boolean, nextCheck?: number) => {
      form.reset(
        {},
        {
          keepValues: true,
        },
      )
      form.setValue('amount', '')
      form.setValue('issuedDate', '')
      form.setValue('issuedPayee', '')
      form.setValue('checkNumber', autoIncrement ? nextCheck?.toString() ?? '' : '')
    },
    [form],
  )

  const handleAddCheckToTable = useCallback(
    (value: FormValues) => {
      const newCheck: ICheck = {
        ...value,
        accountName: accounts.find((item) => item.accountId === value.accountId)?.accountName ?? '',
      }
      setCheckList((prev) => lodash.orderBy([newCheck, ...prev], ['accountName', 'checkNumber'], 'asc'))
    },
    [accounts],
  )

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        setLoading(true)
        const data: IAddIssuedCheck.Payload = {
          accountId: values.accountId ?? '',
          clientId: values.clientId ?? '',
          checkNumber: values.checkNumber,
          amount: parseFloat(values.amount),
          issuedDate: dayjs(values.issuedDate).format('YYYY-MM-DD'),
          autoIncrement: values.autoIncrement,
          issuedPayee: values.issuedPayee,
          voidCheck: values.voidCheck,
        }

        const res = await addNewIssuedCheck(data)
        if (res.statusCode === STATUS_CODES.SUCCESS) {
          handleAutoIncrement(data.autoIncrement, res.data.nextCheckNumber)
          handleAddCheckToTable(values)
          api.success({
            message: (
              <RichTextField
                richText={t('AddNewIssuedCheck:title.add_successfully', { checkNumber: data.checkNumber })}
              />
            ),
            placement: 'topRight',
            closeIcon: null,
          })
        }
      } catch (error: any) {
        console.log('ERROR: ', error)
        if (error.statusCode && error.statusCode === SERVER_STATUS_CODES.PPF01_CHECK_NUMBER_DUPLICATED) {
          form.setError('checkNumber', {
            message: t(`SystemMessages:${error.statusCode}`),
            type: 'pattern',
          })
        }
        if (error.statusCode && error.statusCode === SERVER_STATUS_CODES.PPF02_CHECK_NUMBER_DUPLICATED_ON_CORE) {
          api.error({
            message: t(`SystemMessages:${error.statusCode}`, {
              dateCreated: '',
            }),
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [api, form, handleAddCheckToTable, handleAutoIncrement, t],
  )

  // get account list by client ID with role === bank user and clear errors
  useEffect(() => {
    if (clientId && role !== USER_ROLE.CLIENT) {
      form.setValue('accountId', null)
      form.setValue('checkNumber', '')
      form.setValue('amount', '')
      form.setValue('issuedDate', '')
      form.setValue('issuedPayee', '')
      form.clearErrors()
      handleGetAccounts(clientId)
    }
  }, [clientId, form, role, handleGetAccounts])

  // clear error on changing account
  useEffect(() => {
    form.clearErrors()
  }, [accountId, form])

  useEffect(() => {
    if (role !== USER_ROLE.CLIENT) {
      handleGetClients()
    } else {
      handleGetClientInfoByUser()
    }
  }, [role])

  useEffect(() => {
    if (clients.length > 0 && clients[0]?.id && role === USER_ROLE.CLIENT) {
      form.setValue('clientId', clients[0].id)
    }
  }, [clients, form, role])

  return (
    <AddNewIssuedCheckContext.Provider
      value={{
        form,
        clients,
        accounts,
        loading,
        loadingClients,
        disabled: role === USER_ROLE.CLIENT,
        checkList,
        columnDefs,
        onSubmit,
      }}
    >
      {contextHolder}
      {children}
    </AddNewIssuedCheckContext.Provider>
  )
}

export default AddNewIssuedCheckProvider
